import { debounce } from 'throttle-debounce';
import { SETTINGS, CLASSES, ARIAS, ATTRIBUTES, SELECTORS, SEARCH } from '../../utils/constants';
import safeJSONParse from '../../utils/safeJsonParse';
import trapFocus from '../../utils/trapFocus';
import initProductCards from '../../components/product-card';

// The panel and overlay open by removing the invisible/opacity classes from
// the markup (same approach as the mobile nav drawer); the closed panel is
// inert so its input and links leave the tab order.
const setVisible = (el, isVisible) => {
  if (!el) return;
  el.classList.toggle(CLASSES.INVISIBLE, !isVisible);
  el.classList.toggle(CLASSES.OPACITY_0, !isVisible);
};

const buildSuggestUrl = (query, settings) => {
  const params = new URLSearchParams({
    q: query,
    'resources[type]': SEARCH.RESOURCE_TYPES,
    'resources[limit]': SEARCH.REQUEST_LIMIT,
    'resources[limit_scope]': SEARCH.LIMIT_SCOPE,
    'resources[options][fields]': settings.fields,
    'resources[options][unavailable_products]': settings.oos,
    'section_id': SEARCH.SECTION_ID
  });
  // routes.root carries the locale prefix (including hyphenated ones like /en-gb)
  return `${window.Shopify.routes.root}search/suggest?${params}`;
};

const fetchResultsHtml = async (query, settings) => {
  const response = await fetch(buildSuggestUrl(query, settings));
  if (!response.ok) {
    throw new Error(`Predictive search failed: ${response.status}`);
  }
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const sectionEl = html.querySelector(SELECTORS.SEARCH_SECTION);
  if (!sectionEl) {
    throw new Error('Predictive search returned an unexpected response');
  }
  return sectionEl.innerHTML;
};

const getResultsAnnouncement = (resultsEl) => {
  const count = resultsEl.querySelectorAll(SELECTORS.PRODUCT_CARD).length;
  if (!count) return window.theme.strings.searchNoResults;
  return window.theme.strings.searchResultsCount.replace('{{ count }}', count);
};

// Swaps the default (popular/recommended) content for fetched results and
// back, keeping the pinned footer link and the live region in step
const initResultsState = (panelEl, els) => {
  const { inputEl, defaultEl, resultsEl, footerEl, footerLinkEl, statusEl } = els;

  const updateFooter = (hasResults) => {
    if (!footerEl || !footerLinkEl) return;
    const resultsUrl = resultsEl.querySelector(SELECTORS.SEARCH_PRODUCTS)?.dataset.resultsUrl;
    footerEl.hidden = !hasResults || !resultsUrl;
    if (resultsUrl) footerLinkEl.href = resultsUrl;
  };

  const show = (hasResults) => {
    // Keep focus in the panel if the user tabbed into the block being hidden
    if (hasResults && defaultEl.contains(document.activeElement)) inputEl.focus();
    defaultEl.hidden = hasResults;
    resultsEl.hidden = !hasResults;
    panelEl.toggleAttribute(ATTRIBUTES.SEARCH_ACTIVE, hasResults);
    updateFooter(hasResults);
    if (statusEl) statusEl.textContent = hasResults ? getResultsAnnouncement(resultsEl) : '';
  };

  const reset = () => {
    resultsEl.innerHTML = '';
    show(false);
  };

  return { show, reset };
};

const bindPanelEvents = ({ panelEl, toggleEl, overlayEl, inputEl }, { setPanelState, isOpen, handleInput }) => {
  toggleEl.addEventListener('click', () => setPanelState(!isOpen()));
  const closeEls = panelEl.querySelectorAll(SELECTORS.SEARCH_CLOSE);
  closeEls.forEach(closeEl => closeEl.addEventListener('click', () => setPanelState(false)));
  overlayEl?.addEventListener('click', () => setPanelState(false));
  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape' && isOpen()) setPanelState(false);
  });
  inputEl.addEventListener('input', debounce(SETTINGS.SEARCH_DEBOUNCE_DELAY, handleInput));
  trapFocus(panelEl);
};

const initSearchPanel = (panelEl, toggleEl) => {
  const inputEl = panelEl.querySelector(SELECTORS.SEARCH_INPUT);
  const defaultEl = panelEl.querySelector(SELECTORS.SEARCH_DEFAULT);
  const resultsEl = panelEl.querySelector(SELECTORS.SEARCH_RESULTS);
  if (!inputEl || !defaultEl || !resultsEl) return;

  const overlayEl = document.querySelector(SELECTORS.SEARCH_OVERLAY);
  const settings = { ...SEARCH.DEFAULT_SETTINGS, ...safeJSONParse(panelEl.dataset.settings, {}) };
  const results = initResultsState(panelEl, {
    inputEl,
    defaultEl,
    resultsEl,
    footerEl: panelEl.querySelector(SELECTORS.SEARCH_FOOTER),
    footerLinkEl: panelEl.querySelector(SELECTORS.SEARCH_RESULTS_LINK),
    statusEl: panelEl.querySelector(SELECTORS.SEARCH_STATUS)
  });
  let latestQuery = '';

  const renderResults = async (query) => {
    latestQuery = query;
    try {
      const resultsHtml = await fetchResultsHtml(query, settings);
      // A slower earlier request must not overwrite the newer term's results
      if (query !== latestQuery) return;
      resultsEl.innerHTML = resultsHtml;
      initProductCards();
      results.show(true);
    } catch (error) {
      if (query !== latestQuery) return;
      console.error('Predictive search error:', error);
      latestQuery = '';
      results.reset();
    }
  };

  const handleInput = () => {
    const query = inputEl.value.trim();
    if (query.length < settings.trigger) {
      latestQuery = '';
      results.reset();
      return;
    }
    renderResults(query);
  };

  const isOpen = () => toggleEl.getAttribute(ARIAS.EXPANDED) === 'true';

  const setPanelState = (shouldOpen) => {
    setVisible(panelEl, shouldOpen);
    setVisible(overlayEl, shouldOpen);
    panelEl.toggleAttribute(ATTRIBUTES.INERT, !shouldOpen);
    toggleEl.setAttribute(ARIAS.EXPANDED, shouldOpen);
    if (shouldOpen) {
      // The carousel was measured while hidden, so refresh its layout
      panelEl.querySelector(SELECTORS.SWIPER_DESKTOP)?.swiper?.update();
      inputEl.focus();
      return;
    }
    inputEl.value = '';
    latestQuery = '';
    results.reset();
    toggleEl.focus();
  };

  bindPanelEvents({ panelEl, toggleEl, overlayEl, inputEl }, { setPanelState, isOpen, handleInput });
};

const initSearch = () => {
  const panelEl = document.querySelector(SELECTORS.SEARCH_PANEL);
  const toggleEl = document.querySelector(SELECTORS.SEARCH_TOGGLE);
  if (!panelEl || !toggleEl || panelEl.dataset.searchBound) return;
  panelEl.dataset.searchBound = 'true';
  initSearchPanel(panelEl, toggleEl);
};

export default initSearch;
